/**
 * useAutoFill — React hook that auto-fills a drill plan from the client's posture analysis.
 *
 * Runs both {@link suggestDrillsForClient} and {@link suggestDrillsForClientAI} per slot
 * so the UI can present both result sets to the practitioner.
 *
 * @see docs/features/260331-automated-driller-mvp/KICKOFF_PROMPT.md §4 (Suggest-drills entry point)
 */

import { useState, useCallback } from 'react';
import type { Drill } from '@/components/drill-library/types/drill-types';
import type { Client, AnalysisResultItem } from '@/lib/types';
import type { DrillPlanTemplateDefinition } from '../lib/program-structure';
import type { DrillPlanDay, DrillPlanSlot } from '../types/drill-plan-types';
import type { OverallClassification } from '@/lib/posture-analysis/types';
import {
  suggestDrillsForClient,
  suggestDrillsForClientAI,
  slotIdToCategory,
} from '../lib/suggest-drills';
import {
  buildDrillSuggestionFilters,
  type DrillSuggestionFilters,
} from '@/lib/posture-analysis/drill-suggestion-filters';
import { normalizePainLevelNrs } from '@/lib/clients/roster-fields';
import logger from '@/lib/logging';

/** One slot's worth of suggestions from both methods. */
export interface SlotSuggestions {
  slotId: string;
  /** Top-1 from posture-based deterministic method. */
  postureBased: Drill | null;
  /** Top-1 from AI/measurement-based method. */
  measurementBased: Drill | null;
  /** Score for AI suggestion (useful for display). */
  measurementScore: number;
}

export interface AutoFillResult {
  days: DrillPlanDay[];
  filters: DrillSuggestionFilters;
  missedSlots: string[];
  /** Per-slot dual suggestions so UI can offer a choice between methods. */
  slotSuggestions: Record<string, SlotSuggestions>;
}

export function useAutoFill() {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Run the auto-fill algorithm for a given template and client.
   *
   * Returns filled days + the suggestion filters snapshot, or throws on error.
   * `days` is pre-filled with the posture-based (deterministic) suggestion.
   * `slotSuggestions` exposes both methods per slot for the UI.
   */
  const autoFill = useCallback(
    async (
      template: DrillPlanTemplateDefinition,
      client: Client,
      drills: Drill[]
    ): Promise<AutoFillResult> => {
      setIsLoading(true);
      const startMs = Date.now();

      try {
        // Build OverallClassification from the client's latest posture analysis
        const latestPosture = client.postureAnalyses
          ?.slice()
          .sort((a, b) => {
            const ta = (a.createdAt as any)?.toMillis?.() ?? 0;
            const tb = (b.createdAt as any)?.toMillis?.() ?? 0;
            return tb - ta;
          })[0];

        const storedPhase = extractPhaseFromClassification(client.classification);
        const pattern = mapPatternString(latestPosture?.classification?.pattern) ?? 'N';
        const lateralized =
          mapLateralizationString(latestPosture?.classification?.lateralized) ?? null;
        const grounded =
          mapLateralizationString(latestPosture?.classification?.groundingSide) ?? null;

        const movementLimitations = extractMovementLimitations(client);

        const overall: OverallClassification = {
          pattern,
          lateralized,
          grounded,
          phase: storedPhase ?? 'P1',
          shortCode: buildShortCode(pattern, lateralized, storedPhase ?? 'P1'),
          movementLimitations,
          confidence: { pattern: 0.8, lateralization: 0.7 },
        };

        const filters = buildDrillSuggestionFilters(overall, {
          liftingExperience: client.liftingExperience,
          painLevel: normalizePainLevelNrs(client),
        });

        // Extract SoP analysis items for the AI method
        const analysisItems = extractAnalysisItems(client);

        // Fill each slot with distinct drills (top-ranked per slot, no duplicates)
        const missedSlots: string[] = [];
        const filledSlots: Record<string, DrillPlanSlot | null> = {};
        const slotSuggestions: Record<string, SlotSuggestions> = {};
        const usedDrillIds = new Set<string>();

        const pickUniqueTop = (
          results: Array<{ drill: Drill; score: number }>
        ): Drill | null => {
          for (const { drill } of results) {
            if (!usedDrillIds.has(drill.id)) return drill;
          }
          return null;
        };

        for (const slotMeta of template.slots) {
          const category = slotMeta.category ?? slotIdToCategory(slotMeta.id);

          // Build filtered drill list: if template has a preferredDomain, restrict to that domain
          const domainFiltered = template.preferredDomain
            ? drills.filter((d) => {
                const domains = (
                  Array.isArray(d.tags?.['Domain']) ? d.tags['Domain'] : [d.tags?.['Domain']]
                ).filter(Boolean) as string[];
                return (
                  domains.some((dom) =>
                    dom.toLowerCase().includes(template.preferredDomain!.toLowerCase())
                  ) ||
                  (d.mainCategory || '').toLowerCase().includes(template.preferredDomain!.toLowerCase())
                );
              })
            : drills;

          const eligibleDrills = domainFiltered.filter((d) => !usedDrillIds.has(d.id));
          const suggestionPoolSize = Math.max(template.slots.length, 5);

          // Deterministic (posture-based)
          const postureResults = suggestDrillsForClient(
            filters,
            category,
            eligibleDrills,
            suggestionPoolSize
          );
          // AI (measurement-based)
          const aiResults = suggestDrillsForClientAI(
            filters,
            category,
            eligibleDrills,
            analysisItems,
            suggestionPoolSize
          );

          const postureDrill = pickUniqueTop(postureResults);
          const aiDrill = pickUniqueTop(aiResults);

          slotSuggestions[slotMeta.id] = {
            slotId: slotMeta.id,
            postureBased: postureDrill,
            measurementBased: aiDrill,
            measurementScore: aiResults[0]?.score ?? 0,
          };

          // Default fill uses posture-based; UI can switch to AI per slot
          const chosenDrill = postureDrill ?? aiDrill;
          if (chosenDrill) {
            filledSlots[slotMeta.id] = makeSlot(chosenDrill.id);
            usedDrillIds.add(chosenDrill.id);
          } else {
            filledSlots[slotMeta.id] = null;
            missedSlots.push(slotMeta.id);
            logger.warn('drill-plan:no-suggestion', {
              slotId: slotMeta.id,
              slotCategory: category,
              limitationsCount: filters.movementLimitations.length,
            });
          }
        }

        // Build days — same slots repeated across all days
        const days: DrillPlanDay[] = template.dayNames.map((name) => ({
          name,
          slots: Object.fromEntries(
            Object.entries(filledSlots)
              .filter(([, v]) => v !== null)
              .map(([k, v]) => [k, v as DrillPlanSlot])
          ),
        }));

        logger.info('drill-plan:auto-fill', {
          templateId: template.id,
          clientId: client.id,
          durationMs: Date.now() - startMs,
          filledCount: Object.values(filledSlots).filter(Boolean).length,
          missedCount: missedSlots.length,
          hasAnalysisData: analysisItems.length > 0,
        });

        return { days, filters, missedSlots, slotSuggestions };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { autoFill, isLoading };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeSlot(drillId: string): DrillPlanSlot {
  return { drillId, sets: '', reps: '', rest: '', tempo: '', notes: '' };
}

function mapPatternString(pattern?: string): 'APT' | 'SB' | 'N' | undefined {
  if (!pattern) return undefined;
  const p = pattern.toUpperCase();
  if (p.includes('APT') || p.includes('ANTERIOR PELVIC')) return 'APT';
  if (p.includes('SB') || p.includes('SWAYBACK')) return 'SB';
  if (p.includes('NEUTRAL') || p.includes('STACKED')) return 'N';
  return 'N';
}

function mapLateralizationString(lat?: string): 'Left' | 'Right' | null | undefined {
  if (!lat) return undefined;
  const l = lat.toLowerCase();
  if (l.includes('left')) return 'Left';
  if (l.includes('right')) return 'Right';
  return null;
}

function extractPhaseFromClassification(
  classification?: string
): 'P1' | 'P2' | 'P3' | undefined {
  if (!classification) return undefined;
  const m = classification.match(/P([123])/i);
  if (m) return `P${m[1]}` as 'P1' | 'P2' | 'P3';
  return undefined;
}

function buildShortCode(
  pattern: 'APT' | 'SB' | 'N',
  lateralized: 'Left' | 'Right' | null,
  phase: 'P1' | 'P2' | 'P3'
): string {
  const parts: string[] = [pattern];
  if (lateralized) parts.push(`${lateralized[0]} Lat`);
  parts.push(phase);
  return parts.join(', ');
}

function extractMovementLimitations(client: Client): string[] {
  const assessments = client.assessments;
  if (!assessments?.length) return [];
  const sorted = [...assessments].sort((a, b) => {
    const ta = (a as any).createdAt?.toMillis?.() ?? 0;
    const tb = (b as any).createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  const latest = sorted[0] as any;
  if (Array.isArray(latest?.movementLimitations)) return latest.movementLimitations;
  return [];
}

/**
 * Flatten the `Analysis` object from the latest SoP assessment into a list of
 * `AnalysisResultItem` for use by `suggestDrillsForClientAI`.
 */
function extractAnalysisItems(client: Client): AnalysisResultItem[] {
  const assessments = client.assessments;
  if (!assessments?.length) return [];

  const sorted = [...assessments].sort((a, b) => {
    const ta = (a as any).createdAt?.toMillis?.() ?? 0;
    const tb = (b as any).createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });

  const latest = sorted[0] as any;
  const analysis = latest?.analysis;
  if (!analysis) return [];

  // Combine left + right + intact arrays
  const items: AnalysisResultItem[] = [
    ...(Array.isArray(analysis.left) ? analysis.left : []),
    ...(Array.isArray(analysis.right) ? analysis.right : []),
    ...(Array.isArray(analysis.intact) ? analysis.intact : []),
  ];

  return items;
}
